import { useCallback, useEffect, useMemo, useState } from "react";
import Spinner from "../Spinner/Spinner";
import { formatPhoneNumber } from "../../utils/phoneNumberUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckDouble, faCircleCheck, faUsers } from "@fortawesome/free-solid-svg-icons";
import ShuttleActionMenu from "../Elements/ShuttleActionMenu";
import {
  activeShuttleParticipants,
  isCancelledShuttleParticipant,
} from "../../utils/shuttleParticipants";

const emptySpot = { name: "", address: "", pickupTime: "" };

const ShuttleManager = ({ eventId }) => {
  const apiBaseUrl = process.env.REACT_APP_API_URL;
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [notificationResult, setNotificationResult] = useState(null);
  const [newSpot, setNewSpot] = useState(emptySpot);
  const [showAddSpot, setShowAddSpot] = useState(false);
  const [editingSpot, setEditingSpot] = useState(null);
  const [driverSelections, setDriverSelections] = useState({});
  const [driverExpectedRiders, setDriverExpectedRiders] = useState({});
  const [expandedSpots, setExpandedSpots] = useState({});

  const apiRequest = useCallback(
    async (path, options = {}) => {
      const token = localStorage.getItem("userToken");
      const response = await fetch(`${apiBaseUrl}${path}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(options.headers || {}),
        },
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.message || "Shuttle manager request failed.");
      }
      return payload;
    },
    [apiBaseUrl],
  );

  const refresh = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");
      const payload = await apiRequest(
        `/api/events/${eventId}/shuttle-manager`,
      );
      setData(payload.manager || payload);
    } catch (err) {
      setError(err.message || "Unable to load shuttle manager.");
    } finally {
      setIsLoading(false);
    }
  }, [apiRequest, eventId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const mutate = async (path, method = "POST", body = {}) => {
    try {
      setIsLoading(true);
      setError("");
      const payload = await apiRequest(path, {
        method,
        body: JSON.stringify(body),
      });
      if (payload.summary) {
        setNotificationResult({
          message: payload.message,
          summary: payload.summary,
        });
      }
      setData(payload.manager || payload);
      return true;
    } catch (err) {
      setError(err.message || "Action failed.");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const toggleDriverInfo = async () => {
    await mutate(
      `/api/events/${eventId}/shuttle-driver-info-visibility`,
      "PUT",
      { visible: !data?.event?.shuttleDriverInfoVisible },
    );
  };

  const pickupSpots = useMemo(
    () =>
      (data?.pickupSpots || []).map((spot) => {
        const participants = activeShuttleParticipants(spot.participants);
        const totalPeople = participants.reduce(
          (sum, participant) => sum + (participant.noOfPpl || 1),
          0,
        );
        return {
          ...spot,
          participants,
          participantCount: participants.length,
          totalPeople,
          isFullyAssigned:
            participants.length > 0 &&
            (spot.assignments || []).length > 0 &&
            (spot.assignments || []).reduce(
              (sum, assignment) => sum + Number(assignment.expectedRiders || 0),
              0,
            ) >= totalPeople,
          overCapacity:
            Boolean(spot.shuttleCapacity) && totalPeople > spot.shuttleCapacity,
        };
      }),
    [data],
  );
  const drivers = useMemo(() => data?.drivers || [], [data]);
  const unassignedParticipants = useMemo(
    () => activeShuttleParticipants(data?.unassignedParticipants),
    [data],
  );
  const cancelledParticipants = useMemo(() => {
    const participantsById = new Map();
    const addParticipant = (participant, pickupSpotName = "Unassigned") => {
      if (!isCancelledShuttleParticipant(participant)) return;
      const key =
        participant.eventParticipationId ||
        participant.id ||
        `${participant.name}-${participant.phone}`;
      participantsById.set(key, { ...participant, pickupSpotName });
    };

    (data?.pickupSpots || []).forEach((spot) =>
      (spot.participants || []).forEach((participant) =>
        addParticipant(participant, spot.name),
      ),
    );
    (data?.unassignedParticipants || []).forEach((participant) =>
      addParticipant(participant),
    );

    return Array.from(participantsById.values()).sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || "")),
    );
  }, [data]);

  const totals = useMemo(() => {
    const assignedPeople = pickupSpots.reduce(
      (sum, spot) => sum + (spot.totalPeople || 0),
      0,
    );
    const unassignedPeople = unassignedParticipants.reduce(
      (sum, participant) => sum + (participant.noOfPpl || 1),
      0,
    );
    return {
      spots: pickupSpots.length,
      assignedPeople,
      unassignedPeople,
      shuttles: pickupSpots.reduce(
        (sum, spot) => sum + (spot.assignments?.length || 0),
        0,
      ),
    };
  }, [pickupSpots, unassignedParticipants]);

  const createSpot = async (e) => {
    e.preventDefault();
    if (!newSpot.name.trim()) {
      setError("Pickup spot name is required.");
      return;
    }
    const saved = await mutate(
      `/api/events/${eventId}/shuttle-pickup-spots`,
      "POST",
      newSpot,
    );
    if (saved) {
      setNewSpot(emptySpot);
      setShowAddSpot(false);
    }
  };

  const saveEditedSpot = async (e) => {
    e.preventDefault();
    if (!editingSpot.name.trim()) {
      setError("Pickup spot name is required.");
      return;
    }
    const saved = await mutate(
      `/api/events/${eventId}/shuttle-pickup-spots/${editingSpot.id}`,
      "PUT",
      {
        name: editingSpot.name.trim(),
        address: editingSpot.address.trim(),
        pickupTime: editingSpot.pickupTime.trim(),
      },
    );
    if (saved) setEditingSpot(null);
  };

  const deleteSpot = async (spot) => {
    if (
      !window.confirm(
        `Delete ${spot.name}? Students and shuttles assigned to this spot will be unassigned.`,
      )
    ) {
      return;
    }
    await mutate(
      `/api/events/${eventId}/shuttle-pickup-spots/${spot.id}`,
      "DELETE",
    );
  };

  const mergeIntoSpot = async (targetSpot) => {
    const source = window.prompt(
      `Enter pickup spot ID(s) to merge into ${targetSpot.name}. Separate multiple IDs with commas.`,
    );
    if (!source) return;
    const sourcePickupSpotIds = source
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);

    await mutate(`/api/events/${eventId}/shuttle-pickup-spots/merge`, "POST", {
      targetPickupSpotId: targetSpot.id,
      sourcePickupSpotIds,
    });
  };

  const assignParticipant = (participantId, pickupSpotId) =>
    mutate(
      `/api/events/${eventId}/shuttle-participants/${participantId}`,
      "PUT",
      {
        pickupSpotId: pickupSpotId ? Number(pickupSpotId) : null,
      },
    );

  const restoreParticipant = async (participant) => {
    const pickupSpotId = window.prompt(
      `Enter the pickup spot ID to restore ${participant.name}.`,
      participant.shuttlePickupSpotId || pickupSpots[0]?.id || "",
    );
    if (!pickupSpotId) return;
    await mutate(
      `/api/events/${eventId}/shuttle-participants/${participant.eventParticipationId}/restore`,
      "POST",
      { pickupSpotId: Number(pickupSpotId) },
    );
  };

  const assignDriverToSpot = async (spotId) => {
    const driverEventParticipationId = driverSelections[spotId];
    if (!driverEventParticipationId) {
      setError("Choose a driver first.");
      return;
    }
    const spot = pickupSpots.find((item) => item.id === spotId);
    const driver = drivers.find(
      (item) => String(item.eventParticipationId) === String(driverEventParticipationId),
    );
    const expectedRiders = Number(
      driverExpectedRiders[spotId] ?? spot?.totalPeople ?? 0,
    );
    const calculatedAlreadyExpected = pickupSpots.reduce(
      (total, item) =>
        total +
        (item.assignments || [])
          .filter(
            (assignment) =>
              String(assignment.driverEventParticipationId) ===
                String(driverEventParticipationId) && item.id !== spotId,
          )
          .reduce(
            (sum, assignment) => sum + Number(assignment.expectedRiders || 0),
            0,
          ),
      0,
    );
    const alreadyExpected = Math.max(
      calculatedAlreadyExpected,
      Number(driver?.assignedExpectedRiders || 0),
    );
    const capacity = Number(driver?.capacity || driver?.canTake || 0);
    if (expectedRiders < 0 || (capacity > 0 && alreadyExpected + expectedRiders > capacity)) {
      setError(
        `${driver?.name || "This driver"} would exceed capacity (${alreadyExpected + expectedRiders}/${capacity}).`,
      );
      return;
    }
    await mutate(`/api/events/${eventId}/shuttle-stop-assignments`, "POST", {
      pickupSpotId: spotId,
      driverEventParticipationId: Number(driverEventParticipationId),
      expectedRiders,
    });
    setDriverSelections({ ...driverSelections, [spotId]: "" });
    setDriverExpectedRiders({ ...driverExpectedRiders, [spotId]: "" });
  };

  const editShuttle = async (assignment) => {
    const vehicleDescription = window.prompt(
      "Vehicle description",
      assignment.vehicleDescription || "Grey Honda Civic",
    );
    if (vehicleDescription === null) return;
    const capacity = window.prompt(
      "Shuttle/driver capacity",
      assignment.capacity || "",
    );
    if (capacity === null) return;
    const expectedRiders = window.prompt(
      "Expected pickups at this location",
      assignment.expectedRiders || "0",
    );
    if (expectedRiders === null) return;

    const updated = await mutate(
      `/api/events/${eventId}/shuttle-stop-assignments/${assignment.id}`,
      "PUT",
      { expectedRiders: Number(expectedRiders) },
    );
    if (!updated) return;

    await mutate(
      `/api/events/${eventId}/shuttles/${assignment.shuttleId}`,
      "PUT",
      {
        vehicleDescription,
        capacity: capacity ? Number(capacity) : null,
      },
    );
  };

  const editExpectedCapacity = async (assignment) => {
    const expectedRiders = window.prompt(
      "Expected pickups at this location",
      assignment.expectedRiders ?? "0",
    );
    if (expectedRiders === null) return;
    const capacity = window.prompt(
      "Driver/shuttle capacity",
      assignment.capacity || "",
    );
    if (capacity === null) return;

    const expectedPayload = {
      expectedRiders: Number(expectedRiders),
    };
    const capacityPayload = {
      vehicleDescription: assignment.vehicleDescription || "",
      capacity: capacity ? Number(capacity) : null,
    };
    const capacityIncreases = Number(capacity || 0) > Number(assignment.capacity || 0);

    if (capacityIncreases) {
      const capacityUpdated = await mutate(
        `/api/events/${eventId}/shuttles/${assignment.shuttleId}`,
        "PUT",
        capacityPayload,
      );
      if (!capacityUpdated) return;
    }

    const expectedUpdated = await mutate(
      `/api/events/${eventId}/shuttle-stop-assignments/${assignment.id}`,
      "PUT",
      expectedPayload,
    );
    if (!expectedUpdated || capacityIncreases) return;

    await mutate(
      `/api/events/${eventId}/shuttles/${assignment.shuttleId}`,
      "PUT",
      capacityPayload,
    );
  };

  const removeAssignment = async (assignment) => {
    if (
      !window.confirm(`Remove ${assignment.driverName} from this pickup spot?`)
    ) {
      return;
    }
    await mutate(
      `/api/events/${eventId}/shuttle-stop-assignments/${assignment.id}`,
      "DELETE",
    );
  };

  const sendStudentPickupInfo = async (pickupSpotId = null) => {
    const label = pickupSpotId
      ? "students at this pickup spot"
      : "all assigned students";
    if (!window.confirm(`Send shuttle pickup info to ${label}?`)) return;
    await mutate(
      `/api/events/${eventId}/shuttle-notifications/students`,
      "POST",
      {
        pickupSpotId,
      },
    );
  };

  const sendDriverAssignments = async ({
    pickupSpotId = null,
    driverEventParticipationId = null,
  } = {}) => {
    const label = driverEventParticipationId
      ? "this driver"
      : pickupSpotId
        ? "drivers assigned to this pickup spot"
        : "all assigned shuttle drivers";
    if (!window.confirm(`Send shuttle assignment info to ${label}?`)) return;
    await mutate(
      `/api/events/${eventId}/shuttle-notifications/drivers`,
      "POST",
      {
        pickupSpotId,
        driverEventParticipationId,
      },
    );
  };

  const refreshDestinationEta = async (spotId) => {
    await mutate(
      `/api/events/${eventId}/shuttle-pickup-spots/${spotId}/refresh-destination-eta`,
      "POST",
    );
  };

  const exportAllLinks = () => {
    const rows = [];
    const seen = new Set();
    const addRow = (person, role, pickupSpot = "") => {
      const key = `${role}-${person.eventParticipationId || person.uniqueCode}`;
      if (!person.uniqueCode || seen.has(key)) return;
      seen.add(key);
      rows.push({
        name: person.name || "",
        role,
        pickupSpot,
        link: `${window.location.origin}/${role === "Driver" ? "r" : "p"}/${person.uniqueCode}`,
        read: person.urlLastViewedAt ? "Yes" : "No",
        notificationSent: person.pickupNotificationSent ? "Yes" : "No",
      });
    };

    (data?.pickupSpots || []).forEach((spot) =>
      (spot.participants || []).forEach((person) =>
        addRow(person, "Student", spot.name || ""),
      ),
    );
    (data?.unassignedParticipants || []).forEach((person) =>
      addRow(person, "Student", "Unassigned"),
    );
    (data?.drivers || []).forEach((person) => addRow(person, "Driver"));

    const headers = ["Name", "Role", "Pickup spot", "Link", "Read", "Notification sent"];
    const csv = [headers, ...rows.map((row) => headers.map((header) => {
      const value = row[header === "Pickup spot" ? "pickupSpot" : header === "Notification sent" ? "notificationSent" : header.toLowerCase()];
      return `"${String(value ?? "").replace(/"/g, '""')}"`;
    }))]
      .map((row) => row.join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${(data?.event?.title || "shuttle-event").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "")}-links.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const driverCapacityState = (spot, driver) => {
    const expectedRiders = Number(
      driverExpectedRiders[spot.id] ?? spot.totalPeople ?? 0,
    );
    const calculatedAlreadyExpected = pickupSpots.reduce(
      (total, item) =>
        total +
        (item.assignments || [])
          .filter(
            (assignment) =>
              String(assignment.driverEventParticipationId) ===
                String(driver.eventParticipationId) && item.id !== spot.id,
          )
          .reduce(
            (sum, assignment) => sum + Number(assignment.expectedRiders || 0),
            0,
          ),
      0,
    );
    const alreadyExpected = Math.max(
      calculatedAlreadyExpected,
      Number(driver.assignedExpectedRiders || 0),
    );
    const alreadyAssignedHere = (spot.assignments || []).some(
      (assignment) =>
        String(assignment.driverEventParticipationId) ===
        String(driver.eventParticipationId),
    );
    const capacity = Number(driver.capacity || driver.canTake || 0);
    return {
      disabled:
        alreadyAssignedHere ||
        (capacity > 0 && alreadyExpected + expectedRiders > capacity),
      full: capacity > 0 && alreadyExpected + expectedRiders > capacity,
    };
  };

  return (
    <div className="space-y-4">
      {isLoading && <Spinner />}
      {error && (
        <div className="alert alert-error sticky top-16 z-30 text-sm shadow-lg" role="alert">
          <span>{error}</span>
          <button type="button" className="font-black" onClick={() => setError("")}>Dismiss</button>
        </div>
      )}
      {notificationResult && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-800 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-black">{notificationResult.message}</p>
              <p className="mt-1 text-xs">
                Sent: {notificationResult.summary.sent} · Failed:{" "}
                {notificationResult.summary.failed} · Skipped:{" "}
                {notificationResult.summary.skipped}
              </p>
            </div>
            <button
              type="button"
              className="text-xs font-bold text-green-900"
              onClick={() => setNotificationResult(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {editingSpot && (
        <form
          onSubmit={saveEditedSpot}
          className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 shadow-sm"
        >
          <div className="flex items-center justify-between gap-3">
            <h4 className="font-black text-indigo-950">Edit pickup spot</h4>
            <button
              type="button"
              className="text-sm font-bold text-gray-600"
              onClick={() => setEditingSpot(null)}
            >
              Cancel
            </button>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <label className="text-xs font-bold text-gray-700">
              Pickup location
              <input
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-normal"
                value={editingSpot.name}
                onChange={(e) =>
                  setEditingSpot({ ...editingSpot, name: e.target.value })
                }
              />
            </label>
            <label className="text-xs font-bold text-gray-700">
              Address
              <input
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-normal"
                value={editingSpot.address}
                onChange={(e) =>
                  setEditingSpot({ ...editingSpot, address: e.target.value })
                }
              />
            </label>
            <label className="text-xs font-bold text-gray-700">
              Pickup time
              <div className="mt-1 flex gap-2">
                <input
                  className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm font-normal"
                  value={editingSpot.pickupTime}
                  onChange={(e) =>
                    setEditingSpot({
                      ...editingSpot,
                      pickupTime: e.target.value,
                    })
                  }
                />
                <button className="rounded-md bg-indigo-700 px-4 py-2 text-sm font-bold text-white">
                  Save
                </button>
              </div>
            </label>
          </div>
        </form>
      )}

      <div className="rounded-xl border border-indigo-100 bg-white p-3 shadow-lg sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-wide text-indigo-700">
              Shuttle setup
            </p>
            <h3 className="text-xl font-black text-gray-900">
              Shuttle Manager
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              Group students by pickup spot, assign drivers/shuttles, and watch
              capacity warnings.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              className={`rounded-lg border px-3 py-2 text-sm font-bold ${
                data?.event?.shuttleDriverInfoVisible
                  ? "border-green-700 bg-green-50 text-green-700"
                  : "border-amber-700 bg-amber-50 text-amber-800"
              }`}
              onClick={toggleDriverInfo}
            >
              {data?.event?.shuttleDriverInfoVisible
                ? "Student driver info: On"
                : "Student driver info: Off"}
            </button>
            {data?.event?.shuttleDashboardEnabled &&
              data?.event?.shuttleDashboardCode && (
                <a
                  className="rounded-lg border border-purple-700 bg-white px-3 py-2 text-center text-sm font-bold text-purple-700 hover:bg-purple-50"
                  href={`/shuttle-dashboard/${data.event.shuttleDashboardCode}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open dashboard
                </a>
              )}
            <button
              type="button"
              className="rounded-lg border border-gray-700 bg-white px-3 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50"
              onClick={exportAllLinks}
            >
              Export all links
            </button>
            <button
              type="button"
              className="rounded-lg border border-green-700 bg-white px-3 py-2 text-sm font-bold text-green-700 hover:bg-green-50"
              onClick={() => sendStudentPickupInfo()}
            >
              Notify all students
            </button>
            <button
              type="button"
              className="rounded-lg border border-sky-700 bg-white px-3 py-2 text-sm font-bold text-sky-700 hover:bg-sky-50"
              onClick={() => sendDriverAssignments()}
            >
              Notify all drivers
            </button>
            <button
              type="button"
              className="rounded-lg bg-indigo-700 px-3 py-2 text-sm font-bold text-white hover:bg-indigo-800"
              onClick={() =>
                mutate(`/api/events/${eventId}/shuttle-pickup-spots/generate`)
              }
            >
              Generate from students
            </button>
            <button
              type="button"
              className="rounded-lg border border-indigo-700 bg-white px-3 py-2 text-sm font-bold text-indigo-700"
              onClick={() => setShowAddSpot(!showAddSpot)}
            >
              {showAddSpot ? "Cancel" : "+ Add spot"}
            </button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
          <MiniStat label="Pickup spots" value={totals.spots} />
          <MiniStat label="Assigned riders" value={totals.assignedPeople} />
          <MiniStat
            label="Unassigned"
            value={totals.unassignedPeople}
            tone="warn"
          />
          <MiniStat
            label="Cancelled"
            value={cancelledParticipants.length}
            tone="danger"
          />
              <MiniStat label="Shuttles" value={totals.shuttles} />
        </div>

        <PickupLocationsTable
          pickupSpots={pickupSpots}
          drivers={drivers}
          driverSelections={driverSelections}
          setDriverSelections={setDriverSelections}
          driverExpectedRiders={driverExpectedRiders}
          setDriverExpectedRiders={setDriverExpectedRiders}
          driverCapacityState={driverCapacityState}
          onAssignDriver={assignDriverToSpot}
          onEditAssignment={editExpectedCapacity}
          onRemoveAssignment={removeAssignment}
          onDeleteSpot={deleteSpot}
        />

        {showAddSpot && (
          <form
            onSubmit={createSpot}
            className="mt-4 grid gap-3 rounded-lg bg-indigo-50 p-3 sm:grid-cols-3"
          >
            <input
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              placeholder="Spot name"
              value={newSpot.name}
              onChange={(e) => setNewSpot({ ...newSpot, name: e.target.value })}
            />
            <input
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              placeholder="Address"
              value={newSpot.address}
              onChange={(e) =>
                setNewSpot({ ...newSpot, address: e.target.value })
              }
            />
            <div className="flex gap-2">
              <input
                className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
                placeholder="Pickup time"
                value={newSpot.pickupTime}
                onChange={(e) =>
                  setNewSpot({ ...newSpot, pickupTime: e.target.value })
                }
              />
              <button className="rounded-md bg-indigo-700 px-3 py-2 text-sm font-bold text-white">
                Save
              </button>
            </div>
          </form>
        )}
      </div>

      {unassignedParticipants.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 shadow-sm sm:p-4">
          <h4 className="font-black text-amber-900">Students needing a pickup spot</h4>
          <p className="mt-1 text-xs text-amber-800">
            Add new students to the event, then assign them here without regenerating existing pickup spots.
          </p>
          <div className="mt-3 space-y-2">
            {unassignedParticipants.map((participant) => (
              <ParticipantAssignmentRow
                key={participant.eventParticipationId}
                participant={participant}
                pickupSpots={pickupSpots}
                onAssign={assignParticipant}
              />
            ))}
          </div>
        </div>
      )}

      {cancelledParticipants.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 shadow-sm sm:p-4">
          <h4 className="font-black text-red-900">
            Cancelled students ({cancelledParticipants.length})
          </h4>
          <p className="mt-1 text-xs text-red-700">
            These students are excluded from driver links, active rider totals,
            and shuttle capacity.
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {cancelledParticipants.map((participant) => (
              <div
                key={
                  participant.eventParticipationId ||
                  participant.id ||
                  `${participant.name}-${participant.phone}`
                }
                className="rounded-lg border border-red-100 bg-white p-3 shadow-sm"
              >
                <p className="font-bold text-gray-900">
                  <a
                    href={`/p/${participant.uniqueCode}?adminPreview=1`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    {participant.name}
                  </a>
                  {participant.urlLastViewedAt && (
                    <span
                      className="tooltip ml-1"
                      data-tip={new Date(participant.urlLastViewedAt).toLocaleString()}
                    >
                      <FontAwesomeIcon icon={faCircleCheck} className="text-blue-500" />
                    </span>
                  )}
                </p>
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <span>{formatPhoneNumber(participant.phone)}</span>
                  <span className="rounded-full bg-[#eaf3fa] px-2 py-0.5 font-black text-[#004a7e]">
                    {participant.noOfPpl || 1} <FontAwesomeIcon icon={faUsers} />
                  </span>
                </div>
                <p className="mt-1 text-xs font-bold text-red-700">
                  Pickup: {participant.pickupSpotName}
                </p>
                <button
                  type="button"
                  className="mt-2 rounded-md bg-red-700 px-2 py-1 text-xs font-bold text-white"
                  onClick={() => restoreParticipant(participant)}
                >
                  Restore ride
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {pickupSpots.map((spot) => (
          <div
            key={spot.id}
            className={`overflow-hidden rounded-xl border bg-white shadow-lg ${
              spot.isFullyAssigned
                ? "border-green-400 ring-1 ring-green-200"
                : "border-gray-100"
            }`}
          >
            <div className="border-b border-gray-100 bg-gray-50 p-3 sm:p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="truncate text-lg font-black text-gray-900">
                      {spot.name}
                    </h4>
                    <span className="rounded bg-gray-200 px-2 py-0.5 text-[11px] font-bold text-gray-700">
                      ID {spot.id}
                    </span>
                    {spot.isFullyAssigned && (
                      <span className="rounded bg-green-600 px-2 py-0.5 text-[11px] font-bold text-white">
                        Driver assigned
                      </span>
                    )}
                  </div>
                  {spot.address ? (
                    <a
                      className="mt-1 block text-sm font-semibold text-[#004a7e] underline"
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(spot.address)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {spot.address}
                    </a>
                  ) : (
                    <p className="mt-1 text-sm text-gray-600">No address set</p>
                  )}
                  {spot.pickupTime && (
                    <p className="mt-1 text-xs font-bold text-indigo-700">
                      Pickup: {spot.pickupTime}
                    </p>
                  )}
                  {spot.destinationEtaMinutes && (
                    <p className="mt-1 text-xs font-bold text-green-700">
                      To destination: {spot.destinationEtaMinutes} min
                      {spot.destinationEtaDistanceMiles
                        ? ` · ${spot.destinationEtaDistanceMiles} mi`
                        : ""}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    className="btn btn-xs border-[#004a7e] text-[#004a7e]"
                    onClick={() =>
                      setExpandedSpots((current) => ({
                        ...current,
                        [spot.id]: !current[spot.id],
                      }))
                    }
                  >
                    {expandedSpots[spot.id] ? "Hide details" : "Show details"}
                  </button>
                  <ShuttleActionMenu
                    items={[
                      { label: "Refresh ETA", onClick: () => refreshDestinationEta(spot.id) },
                      ...(spot.assignments.length > 0
                        ? [{
                            label: "Notify drivers at this location",
                            onClick: () => sendDriverAssignments({ pickupSpotId: spot.id }),
                          }]
                        : []),
                      {
                        label: "Edit pickup spot",
                        onClick: () =>
                          setEditingSpot({
                            id: spot.id,
                            name: spot.name || "",
                            address: spot.address || "",
                            pickupTime: spot.pickupTime || "",
                          }),
                      },
                      { label: "Merge pickup spot", onClick: () => mergeIntoSpot(spot) },
                      { label: "Delete pickup spot", danger: true, onClick: () => deleteSpot(spot) },
                    ]}
                  />
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2">
                <MiniStat
                  label="Students at this location"
                  value={spot.totalPeople}
                  compact
                />
                <MiniStat
                  label="Rides assigned"
                  value={(spot.assignments || []).reduce(
                    (sum, assignment) => sum + Number(assignment.expectedRiders || 0),
                    0,
                  )}
                  compact
                  tone={spot.overCapacity ? "danger" : "default"}
                />
              </div>
              {spot.overCapacity && (
                <div className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs font-bold text-red-700">
                  Capacity warning: assigned people exceed shuttle capacity.
                </div>
              )}
            </div>

            {expandedSpots[spot.id] && (
              <div className="space-y-4 p-3 sm:p-4">
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <h5 className="text-sm font-black text-gray-900">
                    Assigned shuttles
                  </h5>
                </div>
                <div className="space-y-2">
                  {spot.assignments.length === 0 && (
                    <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-500">
                      No shuttles assigned yet.
                    </p>
                  )}
                  {spot.assignments.map((assignment) => (
                    <div
                      key={assignment.id}
                      className="rounded-lg border border-gray-100 p-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-bold text-gray-900">
                            <a
                              href={`/r/${assignment.driverUniqueCode}?adminPreview=1`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {assignment.driverName}
                            </a>
                            {assignment.driverUrlLastViewedAt && (
                              <span
                                className="tooltip ml-1"
                                data-tip={new Date(assignment.driverUrlLastViewedAt).toLocaleString()}
                              >
                                <FontAwesomeIcon icon={faCircleCheck} className="text-blue-500" />
                              </span>
                            )}
                            {assignment.driverNotificationSent && (
                              <span
                                className="tooltip ml-2"
                                data-tip="Driver assignment notification sent"
                              >
                                <FontAwesomeIcon icon={faCheckDouble} className="text-blue-500" />
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500">
                            {assignment.vehicleDescription || "Vehicle not set"}{" "}
                            · Capacity {assignment.capacity || "—"}
                          </p>
                          <p className="mt-1 text-xs font-bold text-gray-700">
                            Expected / capacity: {assignment.expectedRiders ?? 0}/{assignment.capacity || "—"}
                          </p>
                          <NotificationTag sent={assignment.driverNotificationSent} label="Driver notification" />
                          {assignment.etaMinutes && (
                            <p className="mt-1 text-xs font-bold text-green-700">
                              ETA: {assignment.etaMinutes} min
                              {assignment.etaDistanceMiles
                                ? ` · ${assignment.etaDistanceMiles} mi`
                                : ""}
                            </p>
                          )}
                        </div>
                        <ShuttleActionMenu
                          items={[
                            { label: "Edit vehicle/capacity", onClick: () => editShuttle(assignment) },
                            {
                              label: "Notify driver",
                              onClick: () =>
                                sendDriverAssignments({
                                  driverEventParticipationId: assignment.driverEventParticipationId,
                                }),
                            },
                            { label: "Remove assignment", danger: true, onClick: () => removeAssignment(assignment) },
                          ]}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                  <label className="flex items-center gap-2 rounded-md border border-gray-300 px-2 text-xs font-bold text-gray-600">
                    Expected pickups
                    <input
                      className="w-16 rounded-md px-1 py-2 text-sm font-normal text-gray-900 outline-none"
                      type="number"
                      min="0"
                      value={driverExpectedRiders[spot.id] ?? spot.totalPeople ?? 0}
                      onChange={(e) =>
                        setDriverExpectedRiders({
                          ...driverExpectedRiders,
                          [spot.id]: e.target.value,
                        })
                      }
                    />
                  </label>
                  <select
                    className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-2 text-sm"
                    value={driverSelections[spot.id] || ""}
                    onChange={(e) =>
                      setDriverSelections({
                        ...driverSelections,
                        [spot.id]: e.target.value,
                      })
                    }
                  >
                    <option value="">Assign driver/shuttle</option>
                    {drivers.map((driver) => (
                      (() => {
                        const capacityState = driverCapacityState(spot, driver);
                        return (
                          <option
                            key={driver.eventParticipationId}
                            value={driver.eventParticipationId}
                            disabled={capacityState.disabled}
                          >
                            {driver.name} · cap {driver.capacity || driver.canTake || "—"}
                            {capacityState.full ? " · over capacity" : ""}
                          </option>
                        );
                      })()
                    ))}
                  </select>
                  <button
                    type="button"
                    className="rounded-md bg-[#004a7e] px-3 py-2 text-sm font-bold text-white"
                    onClick={() => assignDriverToSpot(spot.id)}
                  >
                    Add stop
                  </button>
                </div>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <h5 className="text-sm font-black text-gray-900">Students at this location</h5>
                  {spot.participants.length > 0 && (
                    <ShuttleActionMenu
                      items={[{
                        label: "Notify students",
                        onClick: () => sendStudentPickupInfo(spot.id),
                      }]}
                    />
                  )}
                </div>
                <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
                  {spot.participants.length === 0 && (
                    <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-500">
                      No students assigned.
                    </p>
                  )}
                  {spot.participants.map((participant) => (
                    <ParticipantAssignmentRow
                      key={participant.eventParticipationId}
                      participant={participant}
                      pickupSpots={pickupSpots}
                      onAssign={assignParticipant}
                    />
                  ))}
                </div>
              </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {pickupSpots.length === 0 && !isLoading && (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-6 text-center text-gray-600">
          No pickup spots yet. Generate from student ride locations or add one
          manually.
        </div>
      )}
    </div>
  );
};

const PickupLocationsTable = ({
  pickupSpots,
  drivers,
  driverSelections,
  setDriverSelections,
  driverExpectedRiders,
  setDriverExpectedRiders,
  driverCapacityState,
  onAssignDriver,
  onEditAssignment,
  onRemoveAssignment,
  onDeleteSpot,
}) => (
  <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
    <div className="border-b border-gray-100 px-4 py-3">
      <h4 className="font-black text-gray-900">Pickup locations overview</h4>
      <p className="mt-1 text-xs text-gray-500">
        Drivers and expected pickups assigned to each location.
      </p>
    </div>
    <table className="table w-full text-sm">
      <thead>
        <tr>
          <th>Pickup location</th>
          <th>Pickup time</th>
          <th>Students</th>
          <th>Assigned drivers</th>
          <th>Total assigned</th>
          <th aria-label="Actions"></th>
        </tr>
      </thead>
      <tbody>
        {pickupSpots.map((spot) => (
          <tr key={spot.id}>
            <td>
              <div className="font-bold text-gray-900">{spot.name}</div>
              <div className="max-w-xs truncate text-xs text-gray-500">
                {spot.address || "No address set"}
              </div>
            </td>
            <td>{spot.pickupTime || "—"}</td>
            <td>{spot.totalPeople}</td>
            <td className="min-w-[260px]">
              <div className="space-y-2">
                {spot.assignments?.length ? (
                  <div className="space-y-1">
                  {spot.assignments.map((assignment) => (
                    <div key={assignment.id} className="flex items-center justify-between gap-2 font-semibold text-gray-700">
                      <span>
                        {assignment.driverName || "Driver"}{" "}
                        <span className="text-gray-500">
                          ({assignment.expectedRiders ?? 0}/{assignment.capacity || "—"})
                        </span>
                      </span>
                      <ShuttleActionMenu
                        items={[
                          { label: "Edit expected/capacity", onClick: () => onEditAssignment(assignment) },
                          { label: "Remove driver", danger: true, onClick: () => onRemoveAssignment(assignment) },
                        ]}
                      />
                    </div>
                  ))}
                  </div>
                ) : <span className="text-gray-400">Not assigned</span>}
                <div className="flex items-center gap-1">
                  <input
                    className="w-14 rounded border border-gray-300 px-1 py-1 text-xs"
                    type="number"
                    min="0"
                    value={driverExpectedRiders[spot.id] ?? spot.totalPeople ?? 0}
                    onChange={(event) =>
                      setDriverExpectedRiders({
                        ...driverExpectedRiders,
                        [spot.id]: event.target.value,
                      })
                    }
                    aria-label="Expected pickups"
                  />
                  <select
                    className="min-w-0 flex-1 rounded border border-gray-300 px-1 py-1 text-xs"
                    value={driverSelections[spot.id] || ""}
                    onChange={(event) =>
                      setDriverSelections({
                        ...driverSelections,
                        [spot.id]: event.target.value,
                      })
                    }
                  >
                    <option value="">Add driver</option>
                    {drivers.map((driver) => {
                      const capacityState = driverCapacityState(spot, driver);
                      return (
                        <option
                          key={driver.eventParticipationId}
                          value={driver.eventParticipationId}
                          disabled={capacityState.disabled}
                        >
                          {driver.name} ({driver.remainingCapacity ?? "—"} left)
                        </option>
                      );
                    })}
                  </select>
                  <button
                    type="button"
                    className="rounded bg-[#004a7e] px-2 py-1 text-xs font-bold text-white"
                    onClick={() => onAssignDriver(spot.id)}
                  >
                    Add
                  </button>
                </div>
              </div>
            </td>
            <td>
              {(spot.assignments || []).reduce(
                (sum, assignment) => sum + Number(assignment.expectedRiders || 0),
                0,
              ) || "—"}
            </td>
            <td>
              <ShuttleActionMenu
                items={[
                  {
                    label: "Delete pickup spot",
                    danger: true,
                    onClick: () => onDeleteSpot(spot),
                  },
                ]}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const NotificationTag = ({ sent, label }) => (
  <span
    className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wide ${
      sent ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"
    }`}
  >
    {label}: {sent ? "Sent" : "Pending"}
  </span>
);

const MiniStat = ({ label, value, compact = false, tone = "default" }) => {
  const toneClass =
    tone === "danger"
      ? "text-red-700"
      : tone === "warn"
        ? "text-amber-700"
        : "text-gray-900";

  return (
    <div className="rounded-lg border border-gray-100 bg-white p-2 text-center shadow-sm">
      <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
        {label}
      </p>
      <p
        className={`${compact ? "text-lg" : "text-xl"} font-black ${toneClass}`}
      >
        {value}
      </p>
    </div>
  );
};

const ParticipantAssignmentRow = ({ participant, pickupSpots, onAssign }) => (
  <div className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm">
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="truncate font-bold text-gray-900">
          <a
            href={`/p/${participant.uniqueCode}?adminPreview=1`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            {participant.name}
            {participant.urlLastViewedAt && (
              <span
                className="tooltip ml-1"
                data-tip={new Date(participant.urlLastViewedAt).toLocaleString()}
              >
                <FontAwesomeIcon icon={faCircleCheck} className="text-blue-500" />
              </span>
            )}
            {participant.pickupNotificationSent && (
              <span
                className="tooltip ml-2"
                data-tip="Student pickup notification sent"
              >
                <FontAwesomeIcon icon={faCheckDouble} className="text-blue-500" />
              </span>
            )}
          </a>
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
          <span>{formatPhoneNumber(participant.phone)}</span>
          <span className="rounded-full bg-[#eaf3fa] px-2 py-0.5 font-black text-[#004a7e]">
            {participant.noOfPpl || 1} <FontAwesomeIcon icon={faUsers} />
          </span>
        </div>
        <NotificationTag sent={participant.pickupNotificationSent} label="Student notification" />
      </div>
      <select
        className="rounded-md border border-gray-300 px-2 py-2 text-sm"
        value={participant.shuttlePickupSpotId || ""}
        onChange={(e) =>
          onAssign(participant.eventParticipationId, e.target.value)
        }
      >
        <option value="">Unassigned</option>
        {pickupSpots.map((spot) => (
          <option key={spot.id} value={spot.id}>
            {spot.name}
          </option>
        ))}
      </select>
    </div>
  </div>
);

export default ShuttleManager;
